import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { LogOut, ShieldCheck, Layers, Menu, X, ClipboardCheck } from "lucide-react";
import HeaderKnowledgeLink from "./HeaderKnowledgeLink";

const NavItem = ({ to, children, testid, mobile = false }) => (
  <NavLink
    to={to}
    data-testid={testid}
    className={({ isActive }) =>
      `${mobile ? "w-full rounded-lg" : ""} px-4 py-2 text-sm tracking-tight transition-colors ${
        isActive
          ? "text-white"
          : "text-zinc-400 hover:text-white"
      }`
    }
  >
    {children}
  </NavLink>
);

export default function Header() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const isActiveMember = user?.status !== "revoked";

  const gapsAuditNav = (mobile = false) =>
    isActiveMember ? (
      <NavItem to="/gaps-audit" testid={mobile ? "mobile-nav-gaps-audit" : "nav-gaps-audit"} mobile={mobile}>
        <span className="inline-flex items-center gap-2">
          <ClipboardCheck className="w-3.5 h-3.5" /> Gaps Audit
        </span>
      </NavItem>
    ) : null;

  return (
    <header className="sticky top-0 z-40 glass">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 h-16 flex items-center justify-between gap-3">
        <Link
          to="/dashboard"
          data-testid="brand-link"
          onClick={() => setMobileOpen(false)}
          className="flex items-center gap-3 group min-w-0"
        >
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}/api/assets/bbh_favicon.png`}
            alt="Business Breakthrough Hub"
            className="w-9 h-9 rounded-md object-contain shrink-0"
          />
          <div className="leading-tight min-w-0">
            <div className="font-display font-extrabold text-[13px] sm:text-[15px] tracking-tighter text-white whitespace-nowrap">
              BUSINESS <span className="text-amber-400">BREAKTHROUGH</span> HUB
            </div>
            <div className="text-[8px] sm:text-[9px] uppercase tracking-[0.3em] text-zinc-500 whitespace-nowrap">
              automate your business using ai
            </div>
          </div>
        </Link>

        <nav className="hidden xl:flex items-center gap-1">
          <NavItem to="/dashboard" testid="nav-dashboard">
            <span className="inline-flex items-center gap-2">
              <Layers className="w-3.5 h-3.5" /> Dashboard
            </span>
          </NavItem>
          {gapsAuditNav()}
          <HeaderKnowledgeLink />
          {user?.role === "admin" && (
            <NavItem to="/admin/tools" testid="nav-admin">
              <span className="inline-flex items-center gap-2">
                <ShieldCheck className="w-3.5 h-3.5" /> Admin
              </span>
            </NavItem>
          )}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <div className="hidden xl:flex flex-col items-end leading-tight">
            <div className="text-[13px] text-white">{user?.name}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              {user?.role}
            </div>
          </div>
          <div className="w-9 h-9 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center text-[13px] text-emerald-400 font-semibold">
            {user?.name?.[0]?.toUpperCase() || "U"}
          </div>
          <button
            data-testid="logout-btn"
            onClick={() => {
              logout();
              nav("/login");
            }}
            className="p-2 text-zinc-500 hover:text-white transition-colors"
            title="Sign out"
          >
            <LogOut className="w-4 h-4" />
          </button>
          <button
            type="button"
            data-testid="mobile-menu-btn"
            aria-label={mobileOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((open) => !open)}
            className="xl:hidden p-2 rounded-lg border border-zinc-800 text-zinc-300 hover:text-white hover:border-emerald-500/40 transition-colors"
          >
            {mobileOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div
          data-testid="mobile-nav-menu"
          className="xl:hidden border-t border-white/10 bg-zinc-950/95 backdrop-blur-xl"
        >
          <nav
            className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex flex-col items-stretch gap-1"
            onClick={() => setMobileOpen(false)}
          >
            <NavItem to="/dashboard" testid="mobile-nav-dashboard" mobile>
              <span className="inline-flex items-center gap-2">
                <Layers className="w-3.5 h-3.5" /> Dashboard
              </span>
            </NavItem>
            {gapsAuditNav(true)}
            <HeaderKnowledgeLink />
            {user?.role === "admin" && (
              <NavItem to="/admin/tools" testid="mobile-nav-admin" mobile>
                <span className="inline-flex items-center gap-2">
                  <ShieldCheck className="w-3.5 h-3.5" /> Admin
                </span>
              </NavItem>
            )}
          </nav>
        </div>
      )}
      <div className="hairline" />
    </header>
  );
}
